<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <!-- ==================== THEO SẢN PHẨM ==================== -->
            <div v-if="!isBillMode">
              <div class="check-filter mb-5">
                <b-row>
                  <b-col md="2" class="mb-3">
                    <label class="check-filter__label">Loại hoa hồng</label>
                    <b-form-select
                      size="sm"
                      v-model="productForm.type"
                      :options="commissionTypeOptions"
                    ></b-form-select>
                  </b-col>
                  <b-col md="6" class="mb-3">
                    <label class="check-filter__label">Sản phẩm</label>
                    <Autosuggest
                      :key="'p-product-' + componentKey"
                      :model="productForm.productName"
                      :suggestions="productSuggestions"
                      placeholder="sản phẩm"
                      :limit="10"
                      @select="onSelectProduct($event)"
                      @change="onProductInputChange"
                      suggestionName="suggestionName"
                    />
                  </b-col>
                  <b-col md="4" class="mb-3" v-if="productForm.type === COMMISSION_TYPE.STORE">
                    <label class="check-filter__label">Cửa hàng</label>
                    <Autosuggest
                      :key="'p-store-' + componentKey"
                      :model="productForm.storeName"
                      :suggestions="filteredStoreOptions"
                      placeholder="cửa hàng"
                      :limit="10"
                      @select="onSelectStore($event)"
                      @change="onStoreInputChange"
                      suggestionName="suggestionName"
                    />
                  </b-col>
                  <b-col md="4" class="mb-3" v-else>
                    <label class="check-filter__label">Bộ phận</label>
                    <b-form-select
                      size="sm"
                      v-model="productForm.departmentId"
                      :options="departmentOptions"
                      value-field="id"
                      text-field="name"
                    >
                      <template v-slot:first>
                        <b-form-select-option :value="null"
                          >-- Tất cả bộ phận --</b-form-select-option
                        >
                      </template>
                    </b-form-select>
                  </b-col>
                </b-row>
                <b-row class="align-items-end">
                  <b-col md="2">
                    <label class="check-filter__label">Từ ngày</label>
                    <date-picker
                      v-model="productForm.fromDate"
                      placeholder="Từ ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                    ></date-picker>
                  </b-col>
                  <b-col md="2">
                    <label class="check-filter__label">Đến ngày</label>
                    <date-picker
                      v-model="productForm.toDate"
                      placeholder="Đến ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                    ></date-picker>
                  </b-col>
                  <b-col md="8">
                    <b-button
                      variant="primary"
                      size="sm"
                      class="px-6 mr-2"
                      :disabled="productLoading"
                      @click="checkProduct"
                    >
                      <i class="fas fa-search mr-1"></i>Kiểm tra
                    </b-button>
                    <b-button variant="light" size="sm" @click="resetProduct">
                      Đặt lại
                    </b-button>
                  </b-col>
                </b-row>
              </div>

              <vcl-table v-if="productLoading" :columns="5"></vcl-table>

              <div v-else-if="productResult">
                <!-- Cảnh báo: vì sao sản phẩm không ra tiền / bị lấy nhóm khác -->
                <b-alert
                  v-for="(warning, index) in productResult.warnings"
                  :key="'pw-' + index"
                  show
                  :variant="warning.level"
                  class="mb-4"
                >
                  {{ warning.message }}
                </b-alert>

                <div class="check-hero mb-5">
                  <div class="check-hero__title">
                    {{ productResult.product.productName }}
                  </div>
                  <div class="check-hero__meta">
                    <span>Mã SP: <strong>{{ productResult.product.productCode }}</strong></span>
                    <span>Danh mục: <strong>{{ productResult.product.productCategory }}</strong></span>
                  </div>
                </div>

                <b-row>
                  <!-- Nhóm hoa hồng chứa sản phẩm, kèm nhóm THẮNG theo §4.11 -->
                  <b-col lg="4" class="mb-5">
                    <div class="check-section">
                      Nhóm hoa hồng
                      <span class="check-section__count">{{
                        productResult.categories.length
                      }}</span>
                    </div>
                    <div v-if="!productResult.categories.length" class="check-empty">
                      Sản phẩm không thuộc nhóm hoa hồng nào
                    </div>
                    <div
                      v-for="category in productResult.categories"
                      :key="category.id"
                      class="group-card"
                      :class="{
                        'group-card--win': category.id === productResult.winningCategoryId,
                      }"
                    >
                      <div class="group-card__name">
                        {{ category.name }}
                        <b-badge
                          v-if="category.id === productResult.winningCategoryId"
                          variant="success"
                          class="ml-1"
                          >Áp dụng</b-badge
                        >
                      </div>
                      <div class="group-card__meta">
                        {{ category.viaName }} · tạo
                        {{ displayDate(category.createdAt) }}
                      </div>
                    </div>
                  </b-col>

                  <!-- Chương trình + bậc điều kiện + mức theo vị trí -->
                  <b-col lg="8" class="mb-5">
                    <div class="check-section">
                      Chương trình áp dụng
                      <span class="check-section__count">{{
                        productResult.programs.length
                      }}</span>
                    </div>
                    <div v-if="!productResult.programs.length" class="check-empty">
                      Không có chương trình nào áp cho sản phẩm trong phạm vi và kỳ
                      đã chọn
                    </div>

                    <div
                      v-for="program in productResult.programs"
                      :key="program.commissionProgramId"
                      class="check-card mb-4"
                    >
                      <div class="check-card__head">
                        <span>{{ program.commissionProgramName }}</span>
                        <span class="check-card__sub">
                          {{ displayDate(program.startDate) }} —
                          {{ displayDate(program.endDate) }}
                        </span>
                      </div>

                      <template v-for="(detail, index) in program.details">
                        <!-- Dải ngăn giữa phần TÍNH TIỀN và phần BỊ BỎ QUA -->
                        <div
                          v-if="isFirstIgnored(program.details, index)"
                          :key="'sep-' + detail.commissionProgramDetailId"
                          class="tier-divider"
                        >
                          Không tính — thuộc nhóm không được áp dụng
                        </div>
                        <div
                          :key="detail.commissionProgramDetailId"
                          class="tier"
                          :class="{ 'tier--ignored': !detail.isWinningCategory }"
                        >
                          <div class="tier__head">
                            <span class="tier__group">{{
                              detail.commissionCategoryName
                            }}</span>
                            <span class="tier__condition">{{ detail.conditionText }}</span>
                          </div>
                          <div v-if="!detail.employeeRates.length" class="text-muted small">
                            Chưa cấu hình mức cho vị trí nào
                          </div>
                          <div v-else class="rate-chips">
                            <div
                              v-for="rate in detail.employeeRates"
                              :key="rate.jobTitleId || rate.jobTitleName"
                              class="rate-chip"
                            >
                              <span class="rate-chip__label">{{ rate.jobTitleName }}</span>
                              <span class="rate-chip__value">
                                <template v-if="rate.rateType === RATE_TYPE.MONEY">{{
                                  displayAmount(rate.amount)
                                }}</template>
                                <template v-else>{{ rate.percent }}%</template>
                              </span>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-else class="check-placeholder">
                <i class="fas fa-search"></i>
                <div>Chọn sản phẩm và phạm vi rồi bấm Kiểm tra</div>
              </div>
            </div>

            <!-- ==================== THEO HOÁ ĐƠN ==================== -->
            <div v-else>
              <b-form class="check-filter mb-5" @submit.prevent="checkBill">
                <label class="check-filter__label">Số hoá đơn</label>
                <div class="d-flex">
                  <b-form-input
                    size="sm"
                    class="check-filter__bill mr-3"
                    v-model="billForm.billNumber"
                    placeholder="Nhập số hoá đơn"
                    autocomplete="off"
                  ></b-form-input>
                  <b-button
                    variant="primary"
                    size="sm"
                    class="px-6 mr-2"
                    :disabled="billLoading"
                    @click="checkBill"
                  >
                    <i class="fas fa-search mr-1"></i>Kiểm tra
                  </b-button>
                  <b-button variant="light" size="sm" @click="resetBill">
                    Đặt lại
                  </b-button>
                </div>
              </b-form>

              <vcl-table v-if="billLoading" :columns="6"></vcl-table>

              <div v-else-if="billResult">
                <b-alert
                  v-for="(warning, index) in billResult.warnings"
                  :key="'bw-' + index"
                  show
                  :variant="warning.level"
                  class="mb-4"
                >
                  {{ warning.message }}
                </b-alert>

                <div class="bill-summary mb-5">
                  <div class="bill-summary__total">
                    <div class="bill-summary__label">Tổng hoa hồng</div>
                    <div
                      class="bill-summary__total-value"
                      :class="amountClass(billResult.totals.amount)"
                    >
                      {{ displayAmount(billResult.totals.amount) }}
                    </div>
                  </div>
                  <div class="bill-summary__info">
                    <div>
                      <div class="bill-summary__label">Hoá đơn</div>
                      <div class="bill-summary__value">
                        {{ billResult.bill.billNumber }}
                      </div>
                    </div>
                    <div>
                      <div class="bill-summary__label">Cửa hàng</div>
                      <div class="bill-summary__value">
                        {{ billResult.bill.storeName }}
                      </div>
                    </div>
                    <div>
                      <div class="bill-summary__label">Ngày</div>
                      <div class="bill-summary__value">
                        {{ displayDate(billResult.bill.billDate) }}
                      </div>
                    </div>
                  </div>
                </div>

                <b-row>
                  <!-- Gom theo người nhận: ai được bao nhiêu -->
                  <b-col lg="4" class="mb-5">
                    <div class="check-section">
                      Theo nhân viên
                      <span class="check-section__count">{{
                        billResult.byEmployee.length
                      }}</span>
                    </div>
                    <div v-if="!billResult.byEmployee.length" class="check-empty">
                      Hoá đơn không phát sinh hoa hồng
                    </div>
                    <div v-else class="check-card">
                      <div
                        v-for="(employee, index) in billResult.byEmployee"
                        :key="'emp-' + index"
                        class="person-row"
                      >
                        <div>
                          <div class="person-row__name">{{ employee.employeeName }}</div>
                          <div class="person-row__meta">
                            {{ employee.employeeCode }} · {{ employee.jobTitleName }} ·
                            SL {{ employee.quantity }}
                          </div>
                        </div>
                        <div
                          class="person-row__amount"
                          :class="amountClass(employee.amount)"
                        >
                          {{ displayAmount(employee.amount) }}
                        </div>
                      </div>
                    </div>
                  </b-col>

                  <!-- Chi tiết từng dòng, GOM THEO SẢN PHẨM: thông tin chung của SP
                       nằm ở đầu khối, bảng bên dưới chỉ còn ai được bao nhiêu. -->
                  <b-col lg="8" class="mb-5">
                    <div class="check-section">
                      Theo sản phẩm
                      <span class="check-section__count">{{
                        billProductGroups.length
                      }}</span>
                    </div>
                    <div v-if="!billProductGroups.length" class="check-empty">
                      Không có dòng hoa hồng
                    </div>

                    <div
                      v-for="group in billProductGroups"
                      :key="group.key"
                      class="check-card mb-4"
                    >
                      <div class="check-card__head">
                        <span>
                          {{ group.productName }}
                          <b-badge
                            :variant="group.direction === -1 ? 'danger' : 'light'"
                            class="ml-1"
                            >{{ group.sourceTypeName }}</b-badge
                          >
                        </span>
                        <span class="check-card__total" :class="amountClass(group.amount)">
                          {{ displayAmount(group.amount) }}
                        </span>
                      </div>
                      <div class="check-card__meta">
                        <span>Doanh thu <strong>{{ displayAmount(group.revenue) }}</strong></span>
                        <span>SL <strong>{{ group.quantity }}</strong></span>
                        <span>Nhóm <strong>{{ group.commissionCategoryName }}</strong></span>
                        <span>CT <strong>{{ group.commissionProgramName }}</strong></span>
                      </div>
                      <b-table
                        class="mb-0"
                        :fields="rowFields"
                        :items="group.rows"
                        small
                        borderless
                      >
                        <template v-slot:cell(employeeName)="row">
                          {{ row.item.employeeName }}
                          <span class="text-muted">({{ row.item.employeeCode }})</span>
                        </template>
                        <template v-slot:cell(rateValue)="row">
                          <span v-if="row.item.rateType === RATE_TYPE.MONEY">
                            {{ displayAmount(row.item.rateValue) }}
                          </span>
                          <span v-else>{{ row.item.rateValue }}%</span>
                        </template>
                        <template v-slot:cell(signedAmount)="row">
                          <span
                            class="font-weight-bold"
                            :class="amountClass(row.item.signedAmount)"
                          >
                            {{ displayAmount(row.item.signedAmount) }}
                          </span>
                        </template>
                      </b-table>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-else class="check-placeholder">
                <i class="fas fa-file-invoice-dollar"></i>
                <div>Nhập số hoá đơn rồi bấm Kiểm tra</div>
              </div>
            </div>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Kiểm tra hoa hồng V2 — bản mới của màn /reward/reward-check.
 *
 * Khác màn cũ ở chỗ mọi con số đến từ CHÍNH commission-engine đang chạy báo cáo
 * (KT-14), nên kiểm tra xong là tin được. Ngoài ra chi tiết hơn:
 *   - Nói rõ sản phẩm vào nhóm bằng đường nào (khai đích danh / qua danh mục)
 *   - Chỉ ra nhóm nào THẮNG khi sản phẩm nằm nhiều nhóm (§4.11), bậc nào bị bỏ qua
 *   - Tab hoá đơn tách rõ từng nguồn (bán hàng / trả hàng / ...) và mức áp cho từng dòng
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import debounce from 'debounce';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastFaile, removeAccents } from '@/utils/common';
// NT-3: dùng lại helper định dạng / ngày tháng của màn báo cáo, không viết lại
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

/** Trùng ENUM.COMMISSION_PROGRAM_TYPE bên API */
const COMMISSION_TYPE = { STORE: 1, CR: 3 };
/** Trùng ENUM.COMMISSION_RATE_TYPE bên API */
const RATE_TYPE = { MONEY: 1, PERCENT: 2 };

export default {
  components: { KTCodePreview, Autosuggest, VclTable, datePicker },
  mixins: [commissionReportMixin],
  data() {
    return {
      COMMISSION_TYPE,
      RATE_TYPE,
      componentKey: 0,

      commissionTypeOptions: [
        { value: COMMISSION_TYPE.STORE, text: 'Ngành cửa hàng' },
        { value: COMMISSION_TYPE.CR, text: 'CR' },
      ],

      // --- Tab sản phẩm ---
      productForm: {
        type: COMMISSION_TYPE.STORE,
        productId: null,
        productName: '',
        storeId: null,
        storeName: '',
        departmentId: null,
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
      },
      productSuggestions: [],
      productLoading: false,
      productResult: null,

      // --- Tab hoá đơn ---
      billForm: { billNumber: '' },
      billLoading: false,
      billResult: null,

      storeOptions: [],
      filteredStoreOptions: [],
      departmentOptions: [],

      // Cột trong khối sản phẩm: SP / nguồn / nhóm / chương trình / doanh thu
      // đã nằm ở header khối nên không lặp lại ở từng dòng nữa.
      rowFields: [
        { key: 'employeeName', label: 'Nhân viên', thStyle: thStyle('40%') },
        { key: 'jobTitleName', label: 'Vai trò', thStyle: thStyle('25%') },
        {
          key: 'signedQuantity',
          label: 'SL',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'rateValue',
          label: 'Mức áp',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'signedAmount',
          label: 'Thành tiền',
          thStyle: thStyle('13%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    /** Mỗi kiểu kiểm tra là một route riêng, dùng chung component này */
    isBillMode() {
      return this.$route.name === 'commission-check-bill';
    },
    pageTitle() {
      return this.isBillMode
        ? 'Kiểm tra hoa hồng theo hoá đơn'
        : 'Kiểm tra hoa hồng theo sản phẩm';
    },
    /**
     * Gom dòng hoa hồng theo (sản phẩm + nguồn): mọi dòng của cùng một sản phẩm
     * dùng chung tên SP / doanh thu / nhóm / chương trình, chỉ khác người nhận.
     * Tách nguồn riêng để dòng bán và dòng trả hàng của cùng SP không lẫn vào nhau.
     */
    billProductGroups() {
      if (!this.billResult) {
        return [];
      }
      const groupByKey = new Map();
      this.billResult.rows.forEach((row) => {
        const key = `${row.productId}|${row.sourceType}|${row.commissionProgramDetailId}`;
        if (!groupByKey.has(key)) {
          groupByKey.set(key, {
            key,
            productName: row.productName,
            sourceTypeName: row.sourceTypeName,
            direction: row.direction,
            revenue: row.revenue,
            commissionCategoryName: row.commissionCategoryName,
            commissionProgramName: row.commissionProgramName,
            quantity: 0,
            amount: 0,
            rows: [],
          });
        }
        const group = groupByKey.get(key);
        // SL của SP là số lượng bán, KHÔNG cộng dồn theo từng người nhận
        group.quantity = row.signedQuantity;
        group.amount += row.signedAmount;
        group.rows.push(row);
      });
      return Array.from(groupByKey.values());
    },
  },
  watch: {
    isBillMode() {
      this.setBreadcrumb();
    },
  },
  created() {
    this.fetchStores();
    this.fetchDepartments();
  },
  mounted() {
    this.setBreadcrumb();
  },
  methods: {
    setBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Hoa hồng', route: '/commission/categories' },
        { title: this.pageTitle },
      ]);
    },
    /** true tại bậc BỊ BỎ QUA đầu tiên — chỗ chèn dải ngăn hai phần */
    isFirstIgnored(details, index) {
      return (
        !details[index].isWinningCategory &&
        (index === 0 || details[index - 1].isWinningCategory)
      );
    },

    // ----- Nạp danh mục dùng chung -----
    fetchStores() {
      ApiService.setHeader();
      ApiService.get('/stores/getStores').then((response) => {
        this.storeOptions = (response.data.data || []).map((item) => ({
          id: item.id,
          name: item.name,
          suggestionName: item.name,
        }));
        this.filteredStoreOptions = [...this.storeOptions];
      });
    },
    fetchDepartments() {
      ApiService.setHeader();
      ApiService.get('/employees/departments').then((response) => {
        this.departmentOptions = response.data.data || [];
      });
    },

    // ----- Autosuggest sản phẩm -----
    onProductInputChange(text) {
      this.productForm.productName = text;
      if (!text) {
        this.productForm.productId = null;
      }
      this.debounceSearchProduct(text);
    },
    debounceSearchProduct: debounce(function (name) {
      this.searchProduct(name);
    }, TIME_TRIGGER),
    searchProduct(name) {
      if (!name) {
        this.productSuggestions = [];
        return;
      }
      ApiService.query('product/searchProductByName', { params: { name } }).then(
        (response) => {
          this.productSuggestions = (response.data.data || []).map((item) => ({
            id: item.id,
            name: item.productName,
            code: item.productCode,
            suggestionName: item.productCode
              ? `${item.productCode} - ${item.productName}`
              : item.productName,
          }));
        },
      );
    },
    onSelectProduct(option) {
      this.productForm.productId = option.item.id;
      this.productForm.productName = option.item.name;
    },

    // ----- Autosuggest cửa hàng -----
    onStoreInputChange(text) {
      this.productForm.storeName = text;
      if (!text) {
        this.productForm.storeId = null;
        this.filteredStoreOptions = [...this.storeOptions];
        return;
      }
      const keyword = removeAccents(text).toLowerCase();
      this.filteredStoreOptions = this.storeOptions.filter(
        (item) => removeAccents(item.name).toLowerCase().indexOf(keyword) > -1,
      );
    },
    onSelectStore(option) {
      this.productForm.storeId = option.item.id;
      this.productForm.storeName = option.item.name;
    },

    // ----- Tab 1: kiểm tra sản phẩm -----
    checkProduct() {
      if (!this.productForm.productId) {
        return makeToastFaile('Vui lòng chọn sản phẩm');
      }
      if (
        this.productForm.type === COMMISSION_TYPE.STORE &&
        !this.productForm.storeId
      ) {
        return makeToastFaile('Vui lòng chọn cửa hàng');
      }
      this.productLoading = true;
      this.productResult = null;

      ApiService.query('commission-checks/product', {
        params: {
          productId: this.productForm.productId,
          type: this.productForm.type,
          storeId: this.productForm.storeId,
          departmentId: this.productForm.departmentId,
          fromDate: this.productForm.fromDate,
          toDate: this.productForm.toDate,
        },
      })
        .then((response) => {
          this.productResult = response.data.data;
        })
        .catch((error) => {
          makeToastFaile(this.getErrorMessage(error, 'Không kiểm tra được sản phẩm'));
        })
        .finally(() => {
          this.productLoading = false;
        });
    },
    resetProduct() {
      this.productForm.productId = null;
      this.productForm.productName = '';
      this.productForm.storeId = null;
      this.productForm.storeName = '';
      this.productForm.departmentId = null;
      this.productResult = null;
      this.productSuggestions = [];
      this.filteredStoreOptions = [...this.storeOptions];
      this.componentKey += 1;
    },

    // ----- Tab 2: kiểm tra hoá đơn -----
    checkBill() {
      const billNumber = (this.billForm.billNumber || '').trim();
      if (!billNumber) {
        return makeToastFaile('Vui lòng nhập số hoá đơn');
      }
      this.billLoading = true;
      this.billResult = null;

      ApiService.query('commission-checks/bill', { params: { billNumber } })
        .then((response) => {
          this.billResult = response.data.data;
        })
        .catch((error) => {
          makeToastFaile(this.getErrorMessage(error, 'Không kiểm tra được hoá đơn'));
        })
        .finally(() => {
          this.billLoading = false;
        });
    },
    resetBill() {
      this.billForm.billNumber = '';
      this.billResult = null;
    },
  },
};
</script>

<style scoped>
.check-filter {
  background: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  padding: 16px 20px;
}
.check-filter__label {
  font-size: 12px;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 4px;
}
.check-filter__bill {
  max-width: 320px;
}

.check-placeholder {
  text-align: center;
  color: #b5b5c3;
  padding: 56px 0;
}
.check-placeholder i {
  font-size: 32px;
  color: #d1d3e0;
  margin-bottom: 12px;
}
.check-empty {
  border: 1px dashed #d6d9e0;
  border-radius: 6px;
  padding: 16px;
  color: #7e8299;
  font-size: 13px;
  text-align: center;
}

.check-hero {
  border-left: 4px solid #3699ff;
  background: #f1faff;
  border-radius: 0 6px 6px 0;
  padding: 14px 20px;
}
.check-hero__title {
  font-size: 16px;
  font-weight: 600;
  color: #181c32;
}
.check-hero__meta {
  font-size: 13px;
  color: #7e8299;
  margin-top: 4px;
}
.check-hero__meta span {
  margin-right: 20px;
}

.check-section {
  font-size: 13px;
  font-weight: 600;
  color: #181c32;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
}
.check-section__count {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  margin-left: 4px;
  border-radius: 10px;
  background: #ecf0f3;
  color: #7e8299;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.group-card {
  border: 1px solid #ecf0f3;
  border-left: 3px solid #d6d9e0;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
}
.group-card--win {
  border-left-color: #1bc5bd;
  background: #f4fdfc;
}
.group-card__name {
  font-weight: 600;
  color: #181c32;
}
.group-card__meta {
  font-size: 12px;
  color: #7e8299;
  margin-top: 2px;
}

.check-card {
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  overflow: hidden;
}
.check-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #f8f9fb;
  padding: 10px 16px;
  font-weight: 600;
  color: #181c32;
}
.check-card__sub {
  font-weight: 400;
  font-size: 12px;
  color: #7e8299;
  white-space: nowrap;
}
.check-card__total {
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}
.check-card__meta {
  font-size: 12px;
  color: #7e8299;
  padding: 8px 16px;
  border-top: 1px solid #ecf0f3;
  border-bottom: 1px solid #ecf0f3;
}
.check-card__meta span {
  margin-right: 16px;
}
.check-card__meta strong {
  color: #3f4254;
}

.tier {
  padding: 12px 16px;
  border-top: 1px solid #ecf0f3;
}
.tier--ignored {
  background: #fafafa;
  opacity: 0.6;
}
.tier__head {
  margin-bottom: 8px;
  font-size: 13px;
}
.tier__group {
  font-weight: 600;
  color: #181c32;
  margin-right: 10px;
}
.tier__condition {
  color: #7e8299;
}
.tier-divider {
  background: #fff4de;
  color: #b8860b;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
  border-top: 1px solid #ecf0f3;
}
.rate-chips {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
}
.rate-chip {
  border: 1px solid #ecf0f3;
  border-radius: 4px;
  padding: 6px 10px;
  background: #fff;
}
.rate-chip__label {
  display: block;
  font-size: 11px;
  color: #7e8299;
}
.rate-chip__value {
  font-weight: 600;
  color: #181c32;
}

.bill-summary {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  overflow: hidden;
}
.bill-summary__total {
  flex: 0 0 260px;
  padding: 16px 20px;
  background: #f1faff;
  border-right: 1px solid #ecf0f3;
}
.bill-summary__total-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  color: #187de4;
}
.bill-summary__info {
  flex: 1 1 360px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: center;
  padding: 16px 20px;
}
.bill-summary__label {
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.bill-summary__value {
  font-size: 14px;
  font-weight: 600;
  color: #181c32;
}

.person-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid #ecf0f3;
}
.person-row:first-child {
  border-top: 0;
}
.person-row__name {
  font-weight: 600;
  color: #181c32;
}
.person-row__meta {
  font-size: 12px;
  color: #7e8299;
}
.person-row__amount {
  font-weight: 700;
  white-space: nowrap;
}
</style>
